/**
 * StatGrid — responsive grid wrapper for `StatCard`s.
 *
 * Per `preview/stat-grid.html`: a multi-column grid (2×N on the spec's
 * Account / Profile screen) of tinted `StatCard` tiles. Collapses to a single
 * column on narrow viewports for the rural single-handed phone case. Owns its
 * prop surface, no `sx` leak.
 */
import { Box } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { spacing } from '../theme/tokens.js';

export interface StatGridProps {
  /** StatCard children. */
  children: ReactNode;
  /** Column count (responsive collapses to 1 on narrow widths). Default 2. */
  columns?: number;
  /** Optional max width cap (px) so tiles stay compact. */
  maxWidth?: number;
  /** Pass-through className for layout composition. */
  className?: string;
}

export const StatGrid = forwardRef<HTMLDivElement, StatGridProps>(function StatGrid(
  { children, columns = 2, maxWidth, className },
  ref,
) {
  return (
    <Box
      ref={ref}
      className={className}
      sx={{
        display: 'grid',
        gap: `${spacing.sm}px`,
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${columns}, 1fr)`,
        },
        ...(maxWidth ? { maxWidth } : null),
      }}
    >
      {children}
    </Box>
  );
});
